import type { ReactNode } from "react";

import type { AssetCopy } from "@/api/types";
import { formatDate } from "@/lib/format";

import styles from "./DocumentLink.module.css";

export interface DocumentLinkProps {
  /** The document where the page found it. */
  url: string;
  /** Our copy of it, reached by a signed link that expires, when one was saved. */
  copy?: AssetCopy | null;
  className?: string;
  children: ReactNode;
}

/** A link to an outside document: our saved copy when there is one, with the original beside it. */
export function DocumentLink({ url, copy, className, children }: DocumentLinkProps) {
  return (
    <span className={styles.root}>
      <a href={copy?.url ?? url} target="_blank" rel="noreferrer" className={className}>
        {children}
      </a>
      {copy ? (
        <a href={url} target="_blank" rel="noreferrer" className={styles.original}>
          original ↗
        </a>
      ) : null}
    </span>
  );
}

/** When the copy was saved, and links to the earlier versions kept since the document changed. */
export function CopyHistory({ copy, what = "copy" }: { copy: AssetCopy; what?: string }) {
  return (
    <>
      Our {what} was saved on {formatDate(copy.saved_at)}
      {copy.versions.length ? (
        <>
          ; earlier versions:{" "}
          {copy.versions.map((version, index) => (
            <span key={version.url}>
              {index ? ", " : null}
              <a href={version.url} target="_blank" rel="noreferrer" className={styles.version}>
                {formatDate(version.saved_at)}
              </a>
            </span>
          ))}
        </>
      ) : null}
      .
    </>
  );
}
